import React, { useCallback, useEffect, useMemo } from 'react'
import { Radio } from '@roo/roo'
import { FEFormData } from '../utils/types'
import { useTranslation } from '@sailor/i18n-web'
import Selector from '@roo/roo/Selector'
import TreeSelect from '@roo/roo0119/TreeSelect'
import ConfigProvider from '@roo/roo0119/ConfigProvider'
import { useRequest } from 'ahooks'
import { fetchStaffList, fetchGroupList } from '../utils/api'
import debounce from 'lodash-es/debounce'
import { getEmailName } from '@/common/utils'
import FormPro, { FormInstance } from '@roo/roo/FormPro'
import { BudgetFeeConfigTypeEnum } from '../utils'
import { useDeptTreeSelect, DeptTreeOption } from '@/hooks'
import styles from './AssignInfoSelector.module.less'

interface AssignInfoSelectorProps {
  value: FEFormData['assignInfo'];
  onChange: (value: AssignInfoSelectorProps['value']) => void;
  form: FormInstance;
  /** 父组件通过 onFieldsChange 监听到的 assignInfo 字段错误状态 */
  hasError?: boolean;
}

const AssignInfoSelector: React.FC<AssignInfoSelectorProps> = ({ value, onChange, form, hasError }) => {
  // hasError 来自父组件，但当 value 已满足校验条件时（三选一），本地判断优先
  const valuePassed = Boolean(value?.assignStaffList?.length || value?.assignDepartmentList?.length || value?.assignGroupList?.length)
  const showError = hasError && !valuePassed
  const { t } = useTranslation()
  const { data: staffListRes, run: getStaffList, loading: staffListLoading } = useRequest((keyword) => fetchStaffList({ pageNo: 1, pageSize: 200, keyword }), { manual: true })
  const { data: groupRes, run: getGroupList, loading: groupListLoading } = useRequest((keyword) => fetchGroupList({ pageNo: 1, pageSize: 200, keyword }), { manual: true })

  const {
    treeOptions: deptTreeOptions,
    init: initDeptTree,
    reset: resetDeptTree,
    handleSearch: handleDeptSearch,
    handlePopupScroll: handleDeptPopupScroll,
  } = useDeptTreeSelect()

  /**
   * 收集某节点下所有子孙节点的 id（不含自身）
   */
  const collectDescendantIds = useCallback((nodes: DeptTreeOption[]): Set<number> => {
    const ids = new Set<number>()
    const traverse = (children: DeptTreeOption[]) => {
      for (const node of children) {
        ids.add(node.value)
        if (node.children?.length) traverse(node.children)
      }
    }
    traverse(nodes)
    return ids
  }, [])

  /**
   * 已选中的父级部门的所有子孙 id 集合 —— 这些节点需置为 disabled
   */
  const disabledDeptIds = useMemo<Set<number>>(() => {
    const selectedIds = new Set((value?.assignDepartmentList ?? []).map(item => item.value))
    if (!selectedIds.size) return new Set()

    const result = new Set<number>()
    const traverse = (nodes: DeptTreeOption[]) => {
      for (const node of nodes) {
        if (selectedIds.has(node.value) && node.children?.length) {
          // 已选节点：将其所有子孙加入 disabled 集合
          collectDescendantIds(node.children).forEach(id => result.add(id))
        }
        if (node.children?.length) traverse(node.children)
      }
    }
    traverse(deptTreeOptions)
    return result
  }, [value?.assignDepartmentList, deptTreeOptions, collectDescendantIds])

  /**
   * 将 disabledDeptIds 应用到 treeData，递归打上 disabled 标记
   */
  const deptTreeDataWithDisabled = useMemo<DeptTreeOption[]>(() => {
    if (!disabledDeptIds.size) return deptTreeOptions
    const applyDisabled = (nodes: DeptTreeOption[]): DeptTreeOption[] =>
      nodes.map(node => ({
        ...node,
        disabled: disabledDeptIds.has(node.value) ? true : undefined,
        children: node.children?.length ? applyDisabled(node.children) : undefined,
      }))
    return applyDisabled(deptTreeOptions)
  }, [deptTreeOptions, disabledDeptIds])

  const amountFrom = FormPro.useWatch('amountFrom', form)

  const voidConfig = amountFrom === BudgetFeeConfigTypeEnum.EXCEL
  useEffect(() => {
    if (voidConfig) {
      handleAssignAllChange('all')
    }
  }, [voidConfig])

  const debouncedGetStaffList = useCallback(debounce(getStaffList, 300), [getStaffList])
  const debouncedGetGroupList = useCallback(debounce(getGroupList, 300), [getGroupList])

  const handleAssignAllChange = (newType: 'all' | 'specific') => {
    const assignAll = newType === 'all'
    onChange({ ...value, assignAll })
  }

  useEffect(() => {
    getStaffList('')
    getGroupList('')
    initDeptTree()
  }, [])

  /** 三选一：任意一个有值时，主动清除 assignInfo 的校验错误 */
  const clearValidateIfPassed = (nextValue: AssignInfoSelectorProps['value']) => {
    if (nextValue.assignStaffList?.length || nextValue.assignDepartmentList?.length || nextValue.assignGroupList?.length) {
      form.setFields([{ name: 'assignInfo', errors: [] }])
    }
  }

  const handleStaffChange = (selectedStaff: {label: string; value: string}[]) => {
    const next = { ...value, assignStaffList: selectedStaff }
    onChange(next)
    clearValidateIfPassed(next)
  }

  /**
   * 从选中列表中过滤掉属于某个已选节点的子孙节点
   * 例：先选子部门 A，再选祖先 B（A 是 B 的子孙），则自动移除 A
   */
  const filterOutDescendants = useCallback((vals: { label: string; value: number }[]) => {
    if (!vals.length) return vals
    const descendantIds = new Set<number>()
    const selectedIds = new Set(vals.map(v => v.value))
    const traverse = (nodes: DeptTreeOption[]) => {
      for (const node of nodes) {
        if (selectedIds.has(node.value) && node.children?.length) {
          collectDescendantIds(node.children).forEach(id => descendantIds.add(id))
        }
        if (node.children?.length) traverse(node.children)
      }
    }
    traverse(deptTreeOptions)
    return descendantIds.size ? vals.filter(v => !descendantIds.has(v.value)) : vals
  }, [deptTreeOptions, collectDescendantIds])

  const handleDeptChange = (vals: { label: string; value: number }[]) => {
    const filteredVals = filterOutDescendants(vals)
    const next = { ...value, assignDepartmentList: filteredVals }
    onChange(next)
    clearValidateIfPassed(next)
    if (!filteredVals.length) resetDeptTree()
  }

  const handleGroupChange = (selectedGroups: {label: string; value: number}[]) => {
    const next = { ...value, assignGroupList: selectedGroups }
    onChange(next)
    clearValidateIfPassed(next)
  }

  if (voidConfig) {
    return (
      <div>
        {t('e_pc_custom_restrict', '使用自定义下发,会按照上传文件内的员工限制规则使用')}
      </div>
    )
  }

  return (
    <div>
      <Radio.Group value={value.assignAll ? 'all' : 'specific'} onChange={handleAssignAllChange}>
        <Radio value="all">{t('e_pc_all_staff', '全部员工')}</Radio>
        <Radio value="specific">{t('e_pc_specific_group', '特定人群')}</Radio>
      </Radio.Group>
      {!value.assignAll && (
        <div style={{ paddingTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ lineHeight: '45px', flexShrink: 0, width: '160px', textAlign: 'right' }}>{t('e_pc_specific_staff', '特定员工')}&ensp;</label>
            <Selector multiple filterable clearable
              onSearch={debouncedGetStaffList}
              style={{ flex: 1 }}
              loading={staffListLoading}
              value={value.assignStaffList}
              onChange={handleStaffChange}
              options={staffListRes?.result.map(item => ({ label: `${item.name}${getEmailName(item.email)}`, value: item.staffId })) || []}
              placeholder={t('e_pc_select_specific_staff', '选择特定员工')}
              rawOption // NOTE: 依赖该属性选中整个option,否则回填会有问题
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: '16px' }}>
            <label style={{ lineHeight: '32px', flexShrink: 0, width: '160px', textAlign: 'right' }}>{t('e_pc_specific_dept', '特定部门')}&ensp;</label>
            <div className={styles.deptTreeSelectWrap}>
              <ConfigProvider config={{ prefixCls: 'roo0119' }}>
                <TreeSelect
                  useNew
                  treeCheckable
                  treeCheckStrictly
                  showCheckedStrategy="SHOW_ALL"
                  treeNodeLabelProp="title"
                  variant="filled"
                  status={showError ? 'error' : undefined}
                  treeData={deptTreeDataWithDisabled}
                  value={value.assignDepartmentList.map(item => ({ label: item.label, value: item.value })) as any}
                  placeholder={t('e_pc_select_specific_dept', '选择特定部门')}
                  classNames={{ popup: { root: 'dept-assign-tree-popup' } } as any}
                  showSearch={{ onSearch: handleDeptSearch }}
                  onChange={(vals) => {
                    handleDeptChange((vals as unknown as { label: string; value: number }[]) || [])
                  }}
                  onPopupScroll={handleDeptPopupScroll}
                  allowClear
                  tagRender={({ label, onClose, closable }) => (
                    <span className="roo-tag roo-tag-gray" style={{ display: 'inline-flex', alignItems: 'center', margin: '2px 4px 2px 0' }}>
                      <span className="roo-tag-text">{label}</span>
                      {closable && (
                        <span className="roo-tag-icon-close" onClick={onClose} style={{ cursor: 'pointer', marginLeft: 4 }}>
                          ✕
                        </span>
                      )}
                    </span>
                  )}
                />
              </ConfigProvider>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', paddingTop: '16px' }}>
            <label style={{ lineHeight: '45px', flexShrink: 0, width: '160px', textAlign: 'right' }}>{t('e_pc_specific_group1', '特定分组')}&ensp;</label>
            <Selector multiple filterable clearable
              onSearch={debouncedGetGroupList}
              style={{ flex: 1 }}
              loading={groupListLoading}
              value={value.assignGroupList}
              options={groupRes?.result.map(item => ({ label: item.name, value: item.id })) || []}
              onChange={handleGroupChange}
              placeholder={t('e_pc_select_specific_group', '选择特定分组')}
              rawOption // NOTE: 依赖该属性选中整个option,否则回填会有问题
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default AssignInfoSelector
